import React, { useCallback, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { useVideoDownloadContext } from '@lyearn/lygo-core';
import { Box, Stack, Switch, Typography } from '@lyearn/ui';

import { AdvancedMenuItemComponentProps } from '../../types/menus';
import { setNodeAttr } from '../../utils';

function AllowForwardingDownloading(
  props: AdvancedMenuItemComponentProps & {
    titleKey?: string;
    descKey?: string;
    showDownload?: boolean;
    showPlaybackSpeedControls?: boolean;
  },
) {
  const {
    view,
    currentSelectionNode,
    pos,
    descKey,
    titleKey,
    showDownload,
    showPlaybackSpeedControls,
  } = props;
  const [allowForwarding, setAllowForwarding] = useState(
    currentSelectionNode.attrs.allowForwarding,
  );
  const [allowDownloading, setAllowDownloading] = useState(
    currentSelectionNode.attrs.allowDownloading,
  );

  const [allowBackgroundVideoPlayback, setAllowBackgroundVideoPlayback] = useState(
    currentSelectionNode.attrs.allowBackgroundVideoPlayback,
  );
  const [allowPlaybackSpeedControl, setAllowPlaybackSpeedControl] = useState(
    currentSelectionNode.attrs.allowPlaybackSpeedControl,
  );

  const { t } = useTranslation();
  const { enableDownloadVideo } = useVideoDownloadContext();

  const onEnabledChangeForwarding = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, value: boolean) => {
      setAllowForwarding(value);
      setNodeAttr(view, pos, 'allowForwarding', value);
      // When allowForwarding is enabled, allowPlaybackSpeedControl is "true" by default and its toggle hidden
      // When allowForwarding is disabled, allowPlaybackSpeedControl toggle is shown and initialised with a "false" value
      if (value) {
        setAllowPlaybackSpeedControl(true);
        setNodeAttr(view, pos, 'allowPlaybackSpeedControl', true);
      } else {
        setAllowPlaybackSpeedControl(false);
        setNodeAttr(view, pos, 'allowPlaybackSpeedControl', false);
      }
    },
    [view, pos],
  );

  const onEnableDownloading = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, value: boolean) => {
      setAllowDownloading(value);
      setNodeAttr(view, pos, 'allowDownloading', value);
    },
    [view, pos],
  );

  const onEnablePlaybackSpeedControl = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, value: boolean) => {
      setAllowPlaybackSpeedControl(value);
      setNodeAttr(view, pos, 'allowPlaybackSpeedControl', value);
    },
    [view, pos],
  );

  const onToggleBackgroundVideoPlayback = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, value: boolean) => {
      setAllowBackgroundVideoPlayback(value);
      setNodeAttr(view, pos, 'allowBackgroundVideoPlayback', value);
    },
    [view, pos],
  );

  return (
    <Stack direction="col" gapY={24}>
      <Box>
        <Box className="flex items-center justify-between">
          <Typography variant="label-02">
            {titleKey ? t(titleKey) : t('Lygo:AdvancedMenu.video.AllowForwarding.title')}
          </Typography>
          <Switch checked={allowForwarding} onChange={onEnabledChangeForwarding} />
        </Box>
        <Typography className="mt-4 text-text-secondary" variant="body-short-03">
          {descKey ? t(descKey) : t('Lygo:AdvancedMenu.video.AllowForwarding.description')}
        </Typography>
      </Box>

      {!allowForwarding && showPlaybackSpeedControls && (
        <Box>
          <Box className="flex items-center justify-between">
            <Typography variant="label-02">
              {t('Lygo:AdvancedMenu.video.AllowPlaybackSpeedControl.title')}
            </Typography>
            <Switch checked={allowPlaybackSpeedControl} onChange={onEnablePlaybackSpeedControl} />
          </Box>
          <Typography className="mt-4 text-text-secondary" variant="body-short-03">
            {t('Lygo:AdvancedMenu.video.AllowPlaybackSpeedControl.description')}
          </Typography>
        </Box>
      )}

      {showDownload && enableDownloadVideo && (
        <Box>
          <Box className="flex items-center justify-between">
            <Typography variant="label-02">
              {t('Lygo:AdvancedMenu.video.AllowDownloading.title')}
            </Typography>
            <Switch checked={allowDownloading} onChange={onEnableDownloading} />
          </Box>
          <Typography className="mt-4 text-text-secondary" variant="body-short-03">
            {t('Lygo:AdvancedMenu.video.AllowDownloading.description')}
          </Typography>
        </Box>
      )}

      {
        <Box>
          <Box className="flex items-center justify-between">
            <Typography variant="label-02">
              {t('Lygo:AdvancedMenu.video.AllowBackgroundVideoPlayback.title')}
            </Typography>
            <Switch
              checked={allowBackgroundVideoPlayback}
              onChange={onToggleBackgroundVideoPlayback}
            />
          </Box>
          <Typography className="mt-4 text-text-secondary" variant="body-short-03">
            {t('Lygo:AdvancedMenu.video.AllowBackgroundVideoPlayback.description')}
          </Typography>
        </Box>
      }
    </Stack>
  );
}

export default AllowForwardingDownloading;
